import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Download, Share2, Loader2, Edit, Calendar, User, Heart, Wind, Thermometer, Activity, Sparkles } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useRef } from "react";
import { useExportToPDF } from "@/hooks/useExportToPDF";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PatientTimeline } from "./components/PatientTimeline";

const visitTypeLabels: Record<string, string> = {
  consultation: "Consulta",
  follow_up: "Retorno",
  emergency: "Emergência",
  procedure: "Procedimento",
  other: "Outro",
};

const statusLabels: Record<string, string> = {
  draft: "Rascunho",
  completed: "Concluído",
  archived: "Arquivado",
};

export default function ClinicalRecordDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const pdfRef = useRef<HTMLDivElement>(null);
  const { exportToPDF, isExporting } = useExportToPDF(pdfRef);

  const { data: record, isLoading } = useQuery({
    queryKey: ["clinical-record", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clinical_records")
        .select(`
          *,
          patients_onboarding(full_name, data_nascimento, cpf, avatar_url)
        `)
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!record) {
    return <div>Prontuário não encontrado</div>;
  }

  const handleExportPDF = async () => {
    await exportToPDF(
      `Prontuario_${record.patients_onboarding.full_name}_${format(
        new Date(record.visit_date),
        "dd-MM-yyyy"
      )}.pdf`
    );
  };

  const anamnesis = (record.anamnesis || {}) as Record<string, string>;
  const physicalExam = (record.physical_exam || {}) as Record<string, unknown>;
  const vitalSigns = (physicalExam.vital_signs || {}) as Record<string, number>;
  const diagnoses = ((record.diagnosis as { records?: { code: string; description: string; type: string }[] })?.records) || [];
  const treatmentPlan = (record.treatment_plan || {}) as Record<string, string>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/app/aplicativo/clinicalrecord")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Prontuário de Atendimento</h1>
            <p className="text-muted-foreground">
              {format(new Date(record.visit_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          {record.status === "draft" && (
            <Button
              variant="outline"
              onClick={() => navigate(`/app/aplicativo/clinicalrecord/${id}/editar`)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Editar
            </Button>
          )}
          <Button variant="outline" onClick={handleExportPDF} disabled={isExporting}>
            <Download className="h-4 w-4 mr-2" />
            {isExporting ? "Exportando..." : "Exportar PDF"}
          </Button>
          <Button variant="outline">
            <Share2 className="h-4 w-4 mr-2" />
            Compartilhar
          </Button>
        </div>
      </div>

      {/* PDF Content (hidden, for export) */}
      <div ref={pdfRef} className="hidden">
        <div className="p-8 space-y-6">
          <div className="text-center border-b pb-4">
            <h1 className="text-2xl font-bold">Prontuário de Atendimento</h1>
            <p className="text-muted-foreground">
              {format(new Date(record.visit_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
            </p>
          </div>
          {/* PDF content will be generated here */}
        </div>
      </div>

      {/* Dados do Paciente e Atendimento */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-700 dark:text-blue-300 flex items-center gap-2">
              <User className="h-5 w-5" />
              Dados do Paciente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarImage src={record.patients_onboarding.avatar_url || ""} />
                <AvatarFallback>
                  <User className="h-5 w-5" />
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold">{record.patients_onboarding.full_name}</p>
                {record.patients_onboarding.cpf && (
                  <p className="text-sm text-muted-foreground">CPF: {record.patients_onboarding.cpf}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-cyan-200">
          <CardHeader>
            <CardTitle className="text-cyan-700 dark:text-cyan-300 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Dados do Atendimento
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <span className="text-sm text-muted-foreground">Tipo:</span>
              <p className="font-semibold">{visitTypeLabels[record.visit_type || "consultation"]}</p>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">Status:</span>
              <Badge
                className={
                  record.status === "completed"
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                    : record.status === "archived"
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                    : "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300"
                }
              >
                {statusLabels[record.status || "draft"]}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timeline de Atendimentos */}
      <PatientTimeline patientId={record.patient_id} currentRecordId={id} />

      {/* Queixa Principal */}
      {record.chief_complaint && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Queixa Principal</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-foreground whitespace-pre-wrap">{record.chief_complaint}</p>
          </CardContent>
        </Card>
      )}

      {/* Anamnese */}
      {(anamnesis.current_illness_history ||
        anamnesis.personal_history ||
        anamnesis.family_history ||
        anamnesis.allergies ||
        anamnesis.current_medications ||
        anamnesis.habits) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Anamnese</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {anamnesis.current_illness_history && (
              <div>
                <h4 className="font-semibold mb-2">História da Doença Atual</h4>
                <p className="text-foreground whitespace-pre-wrap">{anamnesis.current_illness_history}</p>
              </div>
            )}
            {anamnesis.personal_history && (
              <div>
                <h4 className="font-semibold mb-2">Antecedentes Pessoais</h4>
                <p className="text-foreground whitespace-pre-wrap">{anamnesis.personal_history}</p>
              </div>
            )}
            {anamnesis.family_history && (
              <div>
                <h4 className="font-semibold mb-2">Antecedentes Familiares</h4>
                <p className="text-foreground whitespace-pre-wrap">{anamnesis.family_history}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {anamnesis.allergies && (
                <div>
                  <h4 className="font-semibold mb-2">Alergias</h4>
                  <p className="text-foreground whitespace-pre-wrap">{anamnesis.allergies}</p>
                </div>
              )}
              {anamnesis.current_medications && (
                <div>
                  <h4 className="font-semibold mb-2">Medicamentos em Uso</h4>
                  <p className="text-foreground whitespace-pre-wrap">{anamnesis.current_medications}</p>
                </div>
              )}
            </div>
            {anamnesis.habits && (
              <div>
                <h4 className="font-semibold mb-2">Hábitos</h4>
                <p className="text-foreground whitespace-pre-wrap">{anamnesis.habits}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Exame Físico */}
      {(Object.keys(vitalSigns).length > 0 ||
        physicalExam.general_appearance ||
        physicalExam.head_neck ||
        physicalExam.cardiovascular ||
        physicalExam.respiratory ||
        physicalExam.abdomen ||
        physicalExam.musculoskeletal ||
        physicalExam.neurological ||
        physicalExam.skin ||
        physicalExam.other_findings) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Exame Físico</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Sinais Vitais */}
            {Object.keys(vitalSigns).length > 0 && (
              <div>
                <h4 className="font-semibold mb-3">Sinais Vitais</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {vitalSigns.blood_pressure_systolic && vitalSigns.blood_pressure_diastolic && (
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground">Pressão Arterial</p>
                      <p className="font-semibold">
                        {vitalSigns.blood_pressure_systolic}/{vitalSigns.blood_pressure_diastolic} mmHg
                      </p>
                    </div>
                  )}
                  {vitalSigns.heart_rate && (
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Heart className="h-3 w-3" /> FC
                      </p>
                      <p className="font-semibold">{vitalSigns.heart_rate} bpm</p>
                    </div>
                  )}
                  {vitalSigns.respiratory_rate && (
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Wind className="h-3 w-3" /> FR
                      </p>
                      <p className="font-semibold">{vitalSigns.respiratory_rate} rpm</p>
                    </div>
                  )}
                  {vitalSigns.temperature && (
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Thermometer className="h-3 w-3" /> Temperatura
                      </p>
                      <p className="font-semibold">{vitalSigns.temperature} °C</p>
                    </div>
                  )}
                  {vitalSigns.oxygen_saturation && (
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Activity className="h-3 w-3" /> SpO2
                      </p>
                      <p className="font-semibold">{vitalSigns.oxygen_saturation}%</p>
                    </div>
                  )}
                  {vitalSigns.weight && (
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground">Peso</p>
                      <p className="font-semibold">{vitalSigns.weight} kg</p>
                    </div>
                  )}
                  {vitalSigns.height && (
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-muted-foreground">Altura</p>
                      <p className="font-semibold">{vitalSigns.height} m</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <Separator />

            {/* Exame por Sistemas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {physicalExam.general_appearance && (
                <div>
                  <h4 className="font-semibold mb-2">Aspecto Geral</h4>
                  <p className="text-foreground whitespace-pre-wrap">{String(physicalExam.general_appearance)}</p>
                </div>
              )}
              {physicalExam.head_neck && (
                <div>
                  <h4 className="font-semibold mb-2">Cabeça e Pescoço</h4>
                  <p className="text-foreground whitespace-pre-wrap">{String(physicalExam.head_neck)}</p>
                </div>
              )}
              {physicalExam.cardiovascular && (
                <div>
                  <h4 className="font-semibold mb-2">Cardiovascular</h4>
                  <p className="text-foreground whitespace-pre-wrap">{String(physicalExam.cardiovascular)}</p>
                </div>
              )}
              {physicalExam.respiratory && (
                <div>
                  <h4 className="font-semibold mb-2">Respiratório</h4>
                  <p className="text-foreground whitespace-pre-wrap">{String(physicalExam.respiratory)}</p>
                </div>
              )}
              {physicalExam.abdomen && (
                <div>
                  <h4 className="font-semibold mb-2">Abdome</h4>
                  <p className="text-foreground whitespace-pre-wrap">{String(physicalExam.abdomen)}</p>
                </div>
              )}
              {physicalExam.musculoskeletal && (
                <div>
                  <h4 className="font-semibold mb-2">Musculoesquelético</h4>
                  <p className="text-foreground whitespace-pre-wrap">{String(physicalExam.musculoskeletal)}</p>
                </div>
              )}
              {physicalExam.neurological && (
                <div>
                  <h4 className="font-semibold mb-2">Neurológico</h4>
                  <p className="text-foreground whitespace-pre-wrap">{String(physicalExam.neurological)}</p>
                </div>
              )}
              {physicalExam.skin && (
                <div>
                  <h4 className="font-semibold mb-2">Pele e Anexos</h4>
                  <p className="text-foreground whitespace-pre-wrap">{String(physicalExam.skin)}</p>
                </div>
              )}
            </div>

            {physicalExam.other_findings && (
              <div>
                <h4 className="font-semibold mb-2">Outros Achados</h4>
                <p className="text-foreground whitespace-pre-wrap">{String(physicalExam.other_findings)}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Diagnóstico */}
      {diagnoses.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Diagnóstico</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {diagnoses
              .filter((d) => d.type === "primary")
              .map((diagnosis, index) => (
                <div key={index} className="p-4 border rounded-lg bg-primary/5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {diagnosis.code && (
                        <Badge variant="outline" className="mb-2">
                          CID-10: {diagnosis.code}
                        </Badge>
                      )}
                      <p className="font-semibold">Diagnóstico Principal</p>
                      <p className="text-foreground whitespace-pre-wrap">{diagnosis.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            {diagnoses
              .filter((d) => d.type === "secondary")
              .map((diagnosis, index) => (
                <div key={index} className="p-3 border rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {diagnosis.code && (
                        <Badge variant="outline" className="mb-2">
                          CID-10: {diagnosis.code}
                        </Badge>
                      )}
                      <p className="text-sm text-muted-foreground mb-1">Diagnóstico Secundário</p>
                      <p className="text-foreground whitespace-pre-wrap">{diagnosis.description}</p>
                    </div>
                  </div>
                </div>
              ))}
          </CardContent>
        </Card>
      )}

      {/* Plano de Tratamento */}
      {(treatmentPlan.conduct ||
        treatmentPlan.prescriptions ||
        treatmentPlan.exam_requests ||
        treatmentPlan.instructions ||
        treatmentPlan.follow_up) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Plano de Tratamento</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {treatmentPlan.conduct && (
              <div>
                <h4 className="font-semibold mb-2">Condutas</h4>
                <p className="text-foreground whitespace-pre-wrap">{treatmentPlan.conduct}</p>
              </div>
            )}
            {treatmentPlan.prescriptions && (
              <div>
                <h4 className="font-semibold mb-2">Prescrições</h4>
                <p className="text-foreground whitespace-pre-wrap">{treatmentPlan.prescriptions}</p>
              </div>
            )}
            {treatmentPlan.exam_requests && (
              <div>
                <h4 className="font-semibold mb-2">Solicitações de Exames</h4>
                <p className="text-foreground whitespace-pre-wrap">{treatmentPlan.exam_requests}</p>
              </div>
            )}
            {treatmentPlan.instructions && (
              <div>
                <h4 className="font-semibold mb-2">Orientações ao Paciente</h4>
                <p className="text-foreground whitespace-pre-wrap">{treatmentPlan.instructions}</p>
              </div>
            )}
            {treatmentPlan.follow_up && (
              <div>
                <h4 className="font-semibold mb-2">Retorno</h4>
                <p className="text-foreground whitespace-pre-wrap">{treatmentPlan.follow_up}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Observações */}
      {record.observations && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Observações</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-foreground whitespace-pre-wrap">{record.observations}</p>
          </CardContent>
        </Card>
      )}

      {/* Resumo IA */}
      {record.ai_summary && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Resumo Gerado por IA
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-foreground whitespace-pre-wrap">{record.ai_summary}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

